"use client"

import { useState, useEffect, useCallback } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
  MessageSquare,
  Edit2,
  Share2,
  ChevronDown,
  ChevronUp,
  CheckSquare,
  FileText,
  AlignLeft,
  List,
  Loader,
  ImageIcon,
  User,
  Award,
  AlertCircle,
  BookOpen,
  ArrowLeft,
  ChevronRight,
  Bot,
  Save,
} from "lucide-react"
import { evaluateAnswers, getSubmissionDetials } from "./api"
import { generatePresignedUrl } from "../postFileAws"
import { postAws } from "../postAws"
import { BlockMath, InlineMath } from "react-katex"
import ReactMarkdown from "react-markdown"
import "katex/dist/katex.min.css"

const ImageDropdown = ({ imageUrl, title = "View Answer Image" }) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className="mt-4 group">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-5 py-3.5 text-sm font-medium text-left text-white 
                   bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl 
                   hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-md 
                   hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
      >
        <span className="flex items-center">
          <ImageIcon className="w-5 h-5 mr-2" />
          {title}
        </span>
        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-700 transition-transform duration-300">
          {isOpen ? <ChevronUp className="w-4 h-4 text-white" /> : <ChevronDown className="w-4 h-4 text-white" />}
        </div>
      </button>

      {isOpen && (
        <div className="mt-4 transition-all duration-500 ease-in-out transform">
          <div className="relative overflow-hidden rounded-xl shadow-xl bg-white p-1.5 border border-gray-100">
            <img
              src={imageUrl || "/placeholder.svg"}
              alt="Answer"
              className="w-full rounded-lg object-contain hover:scale-[1.02] transition-all duration-300"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </div>
        </div>
      )}
    </div>
  )
}

const getQuestionTypeIcon = (questionType, mcqOptions) => {
  if (mcqOptions && mcqOptions.length > 0) {
    return <CheckSquare size={20} className="text-primary" />
  }
  switch (questionType) {
    case "Short Answer":
    case "Short-Answer":
      return <FileText size={20} className="text-primary" />
    case "Essay":
      return <AlignLeft size={20} className="text-primary" />
    default:
      return <List size={20} className="text-primary" />
  }
}

const renderMathText = (text) => {
  if (!text) return null
  const parts = text.split(/(\$\$[\s\S]*?\$\$|\$[\s\S]*?\$)/)
  return parts.map((part, i) => {
    if (part.startsWith("$$") && part.endsWith("$$")) {
      return <BlockMath key={i}>{part.slice(2, -2)}</BlockMath>
    } else if (part.startsWith("$") && part.endsWith("$")) {
      return <InlineMath key={i}>{part.slice(1, -1)}</InlineMath>
    } else {
      return <span key={i}>{part}</span>
    }
  })
}

const renderMarkdown = (text) => {
  return (
    <ReactMarkdown
      components={{
        h1: ({ node, ...props }) => <h1 className="text-2xl font-bold mt-6 mb-3 text-gray-800" {...props} />,
        h2: ({ node, ...props }) => <h2 className="text-xl font-bold mt-5 mb-2 text-gray-800" {...props} />,
        h3: ({ node, ...props }) => <h3 className="text-lg font-semibold mt-4 mb-2 text-gray-800" {...props} />,
        h4: ({ node, ...props }) => <h4 className="text-base font-semibold mt-3 mb-1 text-gray-800" {...props} />,
        p: ({ node, ...props }) => <p className="mb-3 text-gray-700 leading-relaxed" {...props} />,
        ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-3 text-gray-700 space-y-1" {...props} />,
        ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-3 text-gray-700 space-y-1" {...props} />,
        li: ({ node, ...props }) => <li className="mb-1" {...props} />,
        strong: ({ node, ...props }) => <strong className="font-bold text-gray-900" {...props} />,
        a: ({ node, ...props }) => <a className="text-primary hover:underline font-medium" {...props} />,
        blockquote: ({ node, ...props }) => (
          <blockquote className="pl-4 border-l-4 border-primary/30 italic text-gray-600 my-4" {...props} />
        ),
        code: ({ node, ...props }) => (
          <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono text-primary-dark" {...props} />
        ),
      }}
    >
      {text
        .replace(/\n\n### /g, "\n\n# ")
        .replace(/\n\n## /g, "\n\n## ")
        .replace(/\n\n/g, "\n\n&nbsp;\n\n")
        .replace(/\*\*/g, "__")}
    </ReactMarkdown>
  )
}

export default function TeacherAIEvaluation() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [submission, setSubmission] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isSharing, setIsSharing] = useState(false)
  const [results, setResults] = useState(null)
  const [assignment, setAssignment] = useState(null)
  const [isEditing, setIsEditing] = useState({})
  const [showQuestionImage, setShowQuestionImage] = useState({})
  const [questionImages, setQuestionImages] = useState({})
  const [overallRemarks, setOverallRemarks] = useState("")
  const [studentName, setStudentName] = useState("")
  const [editingOverallRemarks, setEditingOverallRemarks] = useState(false)
  const [error, setError] = useState(null)

  const handleBackToAssignments = () => {
    navigate("/assignments")
  }

  const fetchSubmission = useCallback(async () => {
    try {
      const response = await getSubmissionDetials(id)
      const submissionData = response.body.data
      setStudentName(submissionData.studentName)
      setSubmission(submissionData)
      setOverallRemarks(response.body.overallRemarks || "")
      return submissionData
    } catch (err) {
      console.error("Error fetching submission:", err)
      setError("Failed to load submission data. Please try again.")
    }
  }, [id])

  const evaluateAssignment = useCallback(async (assignmentQuestions) => {
    try {
      const response = await evaluateAnswers(assignmentQuestions)
      const data = response.body.data
      setOverallRemarks(response.body.overallRemarks || "")

      const updatedQuestions = await Promise.all(
        data.map(async (question, index) => {
          if (question.questionImageUrl) {
            try {
              const presignedUrl = await generatePresignedUrl(question.questionImageUrl)
              setQuestionImages((prev) => ({ ...prev, [index]: presignedUrl }))
            } catch (error) {
              console.error("Error generating presigned URL for question image:", error)
            }
          }
          if (question.user_image) {
            try {
              const presignedUrl = await generatePresignedUrl(question.user_image)
              return { ...question, user_displayImage: presignedUrl }
            } catch (error) {
              console.error("Error generating presigned URL for user image:", error)
              return question
            }
          }
          return question
        }),
      )

      return updatedQuestions
    } catch (error) {
      console.error("Error evaluating answers:", error)
      setError("Failed to evaluate assignment. Please try again.")
    }
  }, [])

  useEffect(() => {
    const initializeData = async () => {
      if (id) {
        const submissionData = await fetchSubmission()
        if (submissionData?.assignmentQuestion) {
          const evaluatedQuestions = await evaluateAssignment(submissionData.assignmentQuestion)
          setResults(evaluatedQuestions)
          setSubmission((prev) => ({
            ...prev,
            assignmentQuestion: evaluatedQuestions,
            obtainedMarks: evaluatedQuestions.reduce((sum, q) => sum + q.givenMarks, 0),
            status: "evaluated",
          }))
        }
      }
      setLoading(false)
    }

    initializeData()
  }, [id, fetchSubmission, evaluateAssignment])

  useEffect(() => {
    if (results && Array.isArray(results)) {
      const maxMarks = results.reduce((sum, item) => sum + item.maxMarks, 0)
      const obtMarks = results.reduce((sum, item) => sum + item.givenMarks, 0)
      setAssignment({
        id: 1,
        title: submission?.assignment_topic || "AI Evaluated Assignment",
        subject: "Subject",
        studentName: studentName,
        questions: results,
        maximumMarks: maxMarks,
        obtainedMarks: obtMarks,
      })
    }
  }, [results, submission, studentName])

  const handleEdit = (questionId) => {
    setIsEditing((prev) => {
      const newIsEditing = { ...prev }
      newIsEditing[questionId] = !newIsEditing[questionId]

      if (!newIsEditing[questionId]) {
        const updatedQuestions = assignment.questions.map((q, index) =>
          index === questionId ? { ...q, givenMarks: Number(q.givenMarks) } : q,
        )
        setAssignment((prev) => ({
          ...prev,
          questions: updatedQuestions,
          obtainedMarks: updatedQuestions.reduce((sum, q) => sum + q.givenMarks, 0),
        }))
      }

      return newIsEditing
    })
  }

  const handleShareResults = async () => {
    setIsSharing(true)
    try {
      const totalObtainedMarks = assignment.questions.reduce((sum, question) => sum + Number(question.givenMarks), 0)
      const submissionData = {
        id: submission._id,
        body: {
          ...submission,
          status: "evaluated",
          obtainedMarks: totalObtainedMarks,
          overallRemarks: overallRemarks,
          assignmentQuestion: assignment.questions,
        },
        request: "evaluate-Assignment",
      }
      await postAws(`https://api-dev.aurait.ai/submission`, "POST", submissionData)
      navigate(`/assignment/responses/${submission.assignmentId}`)
    } catch (error) {
      console.error("Error sharing results:", error)
      alert("Failed to share results. Please try again.")
    } finally {
      setIsSharing(false)
    }
  }

  const handleQuestionChange = async (question, field, value) => {
    const updatedQuestions = assignment.questions.map((q) =>
      q === question ? { ...q, [field]: field === "givenMarks" ? Number(value) : value } : q,
    )

    await setAssignment((prev) => ({
      ...prev,
      questions: updatedQuestions,
      obtainedMarks: updatedQuestions.reduce((sum, q) => sum + q.givenMarks, 0),
    }))

    setSubmission((prev) => ({
      ...prev,
      assignmentQuestion: updatedQuestions,
    }))
  }

  const toggleQuestionImage = async (questionId) => {
    setShowQuestionImage((prev) => ({ ...prev, [questionId]: !prev[questionId] }))
    if (!showQuestionImage[questionId] && !questionImages[questionId]) {
      try {
        const question = assignment.questions.find((q) => q.id === questionId)
        if (question && question.questionImageUrl) {
          const url = await generatePresignedUrl(question.questionImageUrl)
          setQuestionImages((prev) => ({ ...prev, [questionId]: url }))
        }
      } catch (error) {
        console.error("Error generating presigned URL for question image:", error)
      }
    }
  }

  const handleEditOverallRemarks = () => {
    setEditingOverallRemarks(!editingOverallRemarks)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 flex flex-col justify-center items-center -mt-6">
        <div className="flex flex-col items-center justify-center py-16">
          <div className="relative">
            <Loader className="w-12 h-12 text-indigo-600 animate-spin mb-4" />
            <Bot className="w-6 h-6 text-indigo-400 absolute top-3 left-3" />
          </div>
          <p className="text-gray-800 font-medium text-lg">AI is evaluating student answer</p>
          <p className="text-gray-600 mt-2">Please wait while we process the submission</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-blue-100 -mt-6">
        <div className="bg-white p-8 rounded-xl shadow-xl max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Data</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={handleBackToAssignments}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-300 shadow-md"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  if (!assignment) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-blue-100 -mt-6">
        <div className="bg-white p-8 rounded-xl shadow-xl max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">No Data Found</h2>
          <p className="text-gray-600">No assignment data is available for evaluation.</p>
        </div>
      </div>
    )
  }

  const scorePercentage = Math.round((assignment.obtainedMarks / assignment.maximumMarks) * 100) || 0

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 -mt-6">
      <div className="container mx-auto px-2 md:px-4 py-0 max-w-7xl">
        {/* Header Section with AI Evaluation Info */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-md overflow-hidden mt-6 md:mt-8">
          <div className="bg-gradient-to-r from-indigo-500 to-blue-600 px-3 md:px-4 py-3 md:py-4">
            {/* Mobile Layout */}
            <div className="block lg:hidden">
              {/* Row 1: Breadcrumb and Title */}
              <div className="flex items-center gap-2 mb-3">
                <div className="flex items-center text-white/80 text-xs">
                  <button onClick={handleBackToAssignments} className="hover:text-white transition-colors">
                    Assignments
                  </button>
                  <ChevronRight className="w-3 h-3 mx-1" />
                  <span className="text-white font-medium">AI Evaluation</span>
                </div>
              </div>

              {/* Row 2: Assignment Title */}
              <div className="flex-1 min-w-0 mb-3">
                <h1 className="text-sm font-bold text-white truncate flex items-center">
                  <Bot className="w-4 h-4 mr-2" />
                  {assignment.title}
                </h1>
              </div>

              {/* Row 3: Student Info and Score */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center bg-white/20 backdrop-blur-sm text-white rounded-md px-2 py-1 flex-shrink-0">
                  <User className="w-3 h-3 mr-1" />
                  <span className="text-xs font-medium">{studentName}</span>
                </div>
                <div className="flex items-center bg-white/20 backdrop-blur-sm text-white rounded-md px-2 py-1 flex-shrink-0">
                  <Bot className="w-3 h-3 mr-1" />
                  <span className="text-xs font-medium">AI Evaluated</span>
                </div>
                <div className="flex items-center bg-white/20 backdrop-blur-sm text-white rounded-md px-2 py-1 flex-shrink-0">
                  <Award className="w-3 h-3 mr-1" />
                  <span className="text-xs font-medium">
                    {assignment.obtainedMarks}/{assignment.maximumMarks}
                  </span>
                </div>
              </div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden lg:flex lg:items-center justify-between gap-4">
              {/* Left: Breadcrumb and Title */}
              <div className="flex flex-col gap-1">
                <div className="flex items-center text-white/80 text-sm">
                  <button
                    onClick={handleBackToAssignments}
                    className="hover:text-white transition-colors flex items-center"
                  >
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    Assignments
                  </button>
                  <ChevronRight className="w-4 h-4 mx-1" />
                  <span className="text-white font-medium">AI Evaluation</span>
                </div>
                <h1 className="text-xl lg:text-2xl font-bold text-white flex items-center">
                  <Bot className="w-6 h-6 mr-2" />
                  {assignment.title}
                </h1>
              </div>

              {/* Right: Student Info and Score */}
              <div className="flex items-center gap-4">
                <div className="flex items-center bg-white/20 backdrop-blur-sm text-white rounded-md px-3 py-1.5">
                  <User className="w-4 h-4 mr-2" />
                  <div>
                    <p className="text-xs font-medium mb-0.5">Student</p>
                    <p className="text-sm font-bold">{studentName}</p>
                  </div>
                </div>
                <div className="flex items-center bg-white/20 backdrop-blur-sm text-white rounded-md px-3 py-1.5">
                  <Bot className="w-4 h-4 mr-2" />
                  <div>
                    <p className="text-xs font-medium mb-0.5">Status</p>
                    <p className="text-sm font-bold">AI Evaluated</p>
                  </div>
                </div>
                <div className="flex items-center bg-white/20 backdrop-blur-sm text-white rounded-md px-3 py-1.5">
                  <Award className="w-4 h-4 mr-2" />
                  <div>
                    <p className="text-xs font-medium mb-0.5">Score</p>
                    <p className="text-sm font-bold">
                      {assignment.obtainedMarks}/{assignment.maximumMarks} ({scorePercentage}%)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-3 md:p-6">
            {/* Questions */}
            <div className="space-y-6">
              {assignment.questions.map((question, index) => {
                const isCorrect = Number(question.givenMarks) > 0
                const isIncorrect = Number(question.givenMarks) === 0

                return (
                  <div
                    key={index}
                    className={`bg-white rounded-lg shadow-sm overflow-hidden ${
                      Number(question.givenMarks) > 0 ? "border-2 border-green-500" : "border-2 border-red-500"
                    }`}
                  >
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                      <div className="flex items-center">
                        <div className="p-1.5 rounded-md bg-primary/10 mr-3">
                          {getQuestionTypeIcon(question.questionType, question.mcqOptions)}
                        </div>
                        <h3 className="text-base font-semibold text-gray-800">Question {index + 1}</h3>
                      </div>

                      <div className="flex items-center gap-2">
                        <div
                          className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm ${
                            isCorrect ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                          }`}
                        >
                          <span className="font-medium">
                            {isEditing[index] ? (
                              <input
                                type="number"
                                value={question.givenMarks}
                                onChange={(e) => handleQuestionChange(question, "givenMarks", e.target.value)}
                                className="w-12 px-1 py-0.5 border rounded text-center focus:ring-2 focus:ring-primary focus:border-primary"
                                min="0"
                                max={question.maxMarks}
                              />
                            ) : (
                              question.givenMarks
                            )}{" "}
                            / {question.maxMarks}
                          </span>
                        </div>
                        <button
                          onClick={() => handleEdit(index)}
                          className="p-1.5 text-primary hover:bg-primary/10 rounded-md transition-colors"
                          title={isEditing[index] ? "Save changes" : "Edit score"}
                        >
                          {isEditing[index] ? <Save className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div className="p-4">
                      <div className="text-gray-700 mb-4 prose max-w-none leading-relaxed">
                        {renderMathText(question.question)}
                      </div>

                      {question.questionImageUrl && (
                        <div className="mb-4 group">
                          <button
                            onClick={() => toggleQuestionImage(question.id)}
                            className="flex items-center text-primary hover:text-white transition duration-300 ease-in-out bg-primary/10 hover:bg-primary px-4 py-2.5 rounded-xl"
                          >
                            <ImageIcon className="w-5 h-5 mr-2" />
                            <span className="text-sm font-medium">
                              {showQuestionImage[question.id] ? "Hide Question Image" : "View Question Image"}
                            </span>
                            <div className="ml-2 flex items-center justify-center w-5 h-5 rounded-full bg-primary/20 transition-transform duration-300">
                              {showQuestionImage[question.id] ? (
                                <ChevronUp className="w-3 h-3" />
                              ) : (
                                <ChevronDown className="w-3 h-3" />
                              )}
                            </div>
                          </button>
                          {showQuestionImage[question.id] && questionImages[question.id] && (
                            <div className="mt-4 transition-all duration-500 ease-in-out transform">
                              <div className="relative overflow-hidden rounded-xl shadow-lg bg-white p-1.5 border border-gray-100">
                                <img
                                  src={questionImages[question.id] || "/placeholder.svg"}
                                  alt="Question image"
                                  className="max-w-full h-auto rounded-lg hover:scale-[1.02] transition-all duration-300"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {question.mcqOptions && question.mcqOptions.length > 0 && (
                        <div className="mb-4 bg-gray-50 p-4 rounded-lg border border-gray-100">
                          <h4 className="text-base font-semibold mb-3 text-gray-800 flex items-center">
                            <CheckSquare className="w-4 h-4 mr-2 text-primary" />
                            Multiple Choice Options
                          </h4>
                          <ol className="space-y-2">
                            {question.mcqOptions.map((option, optionIndex) => {
                              const isUserAnswer = option === question.user_answer
                              const isCorrectAnswer = option === question.correct_answer
                              const isBothCorrect = isUserAnswer && isCorrectAnswer

                              return (
                                <li
                                  key={optionIndex}
                                  className={`flex items-start p-3 rounded-lg border-2 ${
                                    isBothCorrect
                                      ? "bg-green-50 border-green-200"
                                      : isCorrectAnswer
                                        ? "bg-green-50 border-green-200"
                                        : isUserAnswer
                                          ? "bg-red-50 border-red-200"
                                          : "bg-white border-gray-200"
                                  }`}
                                >
                                  <div className="flex items-center justify-center min-w-[24px] h-6 rounded-full bg-primary/10 text-primary text-sm font-medium mr-3">
                                    {String.fromCharCode(65 + optionIndex)}
                                  </div>
                                  <div className="flex-1 text-gray-700">{renderMathText(option)}</div>
                                  <div className="flex items-center ml-2 gap-2">
                                    {isUserAnswer && (
                                      <span
                                        className={`text-xs font-medium px-2 py-1 rounded-full ${
                                          isBothCorrect ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                                        }`}
                                      >
                                        Student's Answer
                                      </span>
                                    )}
                                    {isCorrectAnswer && (
                                      <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-100 text-green-700">
                                        Correct Answer
                                      </span>
                                    )}
                                  </div>
                                </li>
                              )
                            })}
                          </ol>
                        </div>
                      )}

                      <div className="mb-4">
                        <h4 className="text-base font-semibold mb-3 text-gray-800 flex items-center">
                          <User className="w-4 h-4 mr-2 text-primary" />
                          Student's Answer
                        </h4>
                        {question.user_answer || question.user_image ? (
                          <div className="space-y-4">
                            {question.user_answer && (
                              <div className="text-gray-700 bg-gray-50 p-4 rounded-lg border-l-4 border-primary">
                                {renderMathText(question.user_answer)}
                              </div>
                            )}
                            {(question.user_displayImage || question.user_image) && (
                              <ImageDropdown imageUrl={question.user_displayImage || question.user_image} />
                            )}
                          </div>
                        ) : (
                          <div className="text-yellow-600 bg-yellow-50 p-4 rounded-lg border border-yellow-200 flex items-center">
                            <AlertCircle className="w-5 h-5 mr-3 text-yellow-500" />
                            No answer was submitted for this question.
                          </div>
                        )}
                      </div>

                      <div className="space-y-4">
                        <div
                          className={`p-4 rounded-lg ${
                            isCorrect ? "bg-green-50 border border-green-100" : "bg-red-50 border border-red-100"
                          }`}
                        >
                          <div className="flex justify-between items-center mb-2">
                            <h4
                              className={`text-base font-semibold flex items-center ${
                                isCorrect ? "text-green-800" : "text-red-800"
                              }`}
                            >
                              <Bot className="w-4 h-4 mr-2" />
                              AI Evaluation
                            </h4>
                            <button
                              onClick={() => handleEdit(index)}
                              className="text-primary hover:text-primary/80 transition-colors"
                              title={isEditing[index] ? "Save changes" : "Edit remarks"}
                            >
                              {isEditing[index] ? <Save className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
                            </button>
                          </div>
                          {isEditing[index] ? (
                            <textarea
                              value={question.remarks}
                              onChange={(e) => handleQuestionChange(question, "remarks", e.target.value)}
                              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                              rows={3}
                              placeholder="Enter your remarks..."
                            />
                          ) : (
                            <p
                              className={`${isCorrect ? "text-green-700" : "text-red-700"} whitespace-pre-line leading-relaxed`}
                            >
                              {question.remarks || "No remarks provided."}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Overall Feedback */}
            <div className="mt-8 bg-gray-50 rounded-lg p-5 border border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold text-gray-900 flex items-center">
                  <MessageSquare className="w-5 h-5 text-primary mr-2" />
                  Overall AI Feedback
                </h2>
                <button
                  onClick={handleEditOverallRemarks}
                  className="flex items-center text-primary hover:text-primary/80 transition-colors"
                >
                  {editingOverallRemarks ? <Save className="w-4 h-4 mr-2" /> : <Edit2 className="w-4 h-4 mr-2" />}
                  {editingOverallRemarks ? "Save" : "Edit"}
                </button>
              </div>
              {editingOverallRemarks ? (
                <textarea
                  value={overallRemarks}
                  onChange={(e) => setOverallRemarks(e.target.value)}
                  className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  rows={6}
                  placeholder="Enter overall feedback..."
                />
              ) : (
                <div className="prose max-w-none bg-white p-4 rounded-lg border border-gray-200">
                  {overallRemarks ? renderMarkdown(overallRemarks) : "No overall feedback provided."}
                </div>
              )}
            </div>

            {/* Assignment Summary */}
            <div className="mt-8 bg-gray-50 rounded-lg p-5 border border-gray-200">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <BookOpen className="w-5 h-5 text-primary mr-2" />
                Evaluation Summary
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-600 mb-1">Total Questions</h3>
                  <p className="text-2xl font-bold text-gray-900">{assignment.questions.length}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-600 mb-1">Questions Correct</h3>
                  <p className="text-2xl font-bold text-green-600">
                    {assignment.questions.filter((q) => Number(q.givenMarks) > 0).length}
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-600 mb-1">Final Score</h3>
                  <p className="text-2xl font-bold text-primary">
                    {assignment.obtainedMarks}/{assignment.maximumMarks} ({scorePercentage}%)
                  </p>
                </div>
              </div>
            </div>

            {/* Share Results Button */}
            <div className="mt-8 flex justify-end">
              <button
                onClick={handleShareResults}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-6 rounded-lg inline-flex items-center transition duration-300 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSharing}
              >
                {isSharing ? (
                  <>
                    <Loader className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                    Sharing...
                  </>
                ) : (
                  <>
                    <Share2 className="w-5 h-5 mr-2" />
                    Share Results with Student
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Loading Overlay */}
      {isSharing && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50">
          <div className="bg-white p-8 rounded-xl shadow-2xl flex flex-col items-center">
            <Loader className="w-16 h-16 text-indigo-600 animate-spin mb-4" />
            <p className="text-xl font-semibold text-gray-800">Sharing results...</p>
            <p className="text-gray-600 mt-2">Please wait while we process your request</p>
          </div>
        </div>
      )}
    </div>
  )
}
